import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { CookieBanner } from "./components/CookieBanner";
import { InstallationGate } from "./components/InstallationGate";
import { InstallationLinkGate } from "./components/InstallationLinkGate";
import { INSTALLATION_ENABLED } from "./config/installation";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";
import { SHORT_LINKS } from "./config/shortLinks";

// Lazy-load non-landing routes to reduce initial JS bundle size.
const Product = lazy(() => import("./pages/Product"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const Comorbidities = lazy(() => import("./pages/Comorbidities"));
const CKD = lazy(() => import("./pages/comorbidities/CKD"));
const DPN = lazy(() => import("./pages/comorbidities/DPN"));
const CHD = lazy(() => import("./pages/comorbidities/CHD"));
const HYP = lazy(() => import("./pages/comorbidities/HYP"));
const InstructionsForUse = lazy(() => import("./pages/InstructionsForUse"));
const Bestellung = lazy(() => import("./pages/Bestellung"));
const Vertrag = lazy(() => import("./pages/Vertrag"));
const PraedigoAccess = lazy(() => import("./pages/PraedigoAccess"));
const Privacy = lazy(() => import("./pages/Privacy"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Activate = lazy(() => import("./pages/Activate"));
const Documents = lazy(() => import("./pages/Documents"));
const Mappings = lazy(() => import("./pages/Mappings"));
const ShortLink = lazy(() => import("./pages/ShortLink"));
const Installation = lazy(() => import("./pages/Installation"));
const InstallationMedatixx = lazy(() => import("./pages/installation/Medatixx"));
const InstallationUnderConstruction = lazy(() => import("./pages/installation/UnderConstruction"));
// Lazy-load sonner toaster: only pulled in when a page actually renders toasts.
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

const queryClient = new QueryClient();

// Redirect legacy /:lang/software|ai-framework|clinical-validation to /:lang/product
const RedirectToProduct = () => {
  const { lang } = useParams<{ lang: string }>();
  return <Navigate to={`/${lang || "en"}/product`} replace />;
};

// Fixed locale routing by domain: praedigo.de serves German, praedigo.com serves
// English. .de root requests are forwarded to the .com domain with the /de prefix.
// No browser language detection is used.
const RootRedirect = () => {
  const { pathname, search } = useLocation();
  const host = window.location.hostname;
  if (host === "praedigo.de" || host === "www.praedigo.de") {
    const rest = pathname.replace(/^\/+/, "");
    // Existing localized paths keep their language; everything else gets /de.
    const target = /^(en|de)(\/|$)/.test(rest)
      ? `https://praedigo.com/${rest}`
      : `https://praedigo.com/de${rest ? `/${rest}` : ""}`;
    window.location.replace(`${target}${search}`);
    return null;
  }
  return <Navigate to="/en" replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Suspense fallback={null}>
      <Sonner />
    </Suspense>
    <BrowserRouter>
      <ScrollToTop />
      <CookieBanner />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/ifu" element={<Navigate to="/en/instructions-for-use" replace />} />
          {Object.keys(SHORT_LINKS).map((slug) => (
            <Route key={slug} path={`/${slug}`} element={<ShortLink />} />
          ))}
          <Route path="/:lang" element={<Index />} />
          <Route path="/:lang/product" element={<Product />} />
          {/* Legacy routes → /product */}
          <Route path="/:lang/software" element={<RedirectToProduct />} />
          <Route path="/:lang/ai-framework" element={<RedirectToProduct />} />
          <Route path="/:lang/clinical-validation" element={<RedirectToProduct />} />
          <Route path="/:lang/blog" element={<Blog />} />
          <Route path="/:lang/contact" element={<Contact />} />
          <Route path="/:lang/comorbidities" element={<Comorbidities />} />
          <Route path="/:lang/comorbidities/ckd" element={<CKD />} />
          <Route path="/:lang/comorbidities/dpn" element={<DPN />} />
          <Route path="/:lang/comorbidities/chd" element={<CHD />} />
          <Route path="/:lang/comorbidities/hyp" element={<HYP />} />
          <Route path="/:lang/bestellung" element={<Bestellung />} />
          <Route path="/:lang/bestellung-staging" element={<Bestellung staging />} />
          <Route path="/:lang/test-registrierung" element={<Bestellung staging />} />
          <Route path="/:lang/vertrag" element={<Vertrag />} />
          <Route path="/:lang/activate" element={<Activate />} />
          <Route path="/:lang/activate/demo" element={<Activate demo />} />
          <Route path="/:lang/dokumente" element={<Documents />} />
          <Route path="/:lang/zuordnungen" element={<Mappings />} />
          <Route path="/:lang/zuordnungen/demo" element={<Mappings demo />} />
          <Route path="/:lang/praedigo-access" element={<PraedigoAccess />} />
          <Route path="/:lang/installation/medatixx" element={<InstallationLinkGate><InstallationMedatixx /></InstallationLinkGate>} />
          {INSTALLATION_ENABLED && (
            <>
              <Route path="/:lang/installation" element={<InstallationGate><Installation /></InstallationGate>} />
              <Route path="/:lang/installation/tomedo" element={<InstallationGate><InstallationUnderConstruction system="tomedo" /></InstallationGate>} />
              <Route path="/:lang/installation/cgm" element={<InstallationGate><InstallationUnderConstruction system="cgm" /></InstallationGate>} />
            </>
          )}
          <Route path="/:lang/ifu" element={<InstructionsForUse />} />
          <Route path="/:lang/instructions-for-use" element={<InstructionsForUse />} />
          <Route path="/:lang/privacy" element={<Privacy />} />
          <Route path="/:lang/legal" element={<LegalNotice />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </QueryClientProvider>
);


export default App;
